import { Suspense, useMemo } from "react";
import { Link, useRoute } from "wouter";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  REPORTS,
  firstReadyReport,
  getReport,
  type DashboardReport,
} from "./registry";

/**
 * Dashboard page shell — the in-house Power BI replacement.
 *
 * Two-pane layout mimicking Power BI:
 *  - LEFT rail (~240px): the report list from REPORTS, grouped by `group`.
 *  - RIGHT canvas: the selected report's lazy component inside <Suspense>.
 *
 * Selection is route-driven:
 *  - /dashboard            → default to the first "ready" report
 *  - /dashboard/:reportId  → that report
 * Clicking a rail item navigates to /dashboard/{id} (Wouter <Link>).
 *
 * This is the SHELL only — the Sales report UI lives in ./reports/SalesReport.tsx.
 * See: projects/nexus-dashboards-powerbi-migration/docs/design.md §3.1
 */

/** Group reports for the rail, preserving registry order of groups + items. */
function useGroupedReports(): { group: string; reports: DashboardReport[] }[] {
  return useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, DashboardReport[]>();
    for (const r of REPORTS) {
      const g = r.group ?? "Reports";
      if (!byGroup.has(g)) {
        byGroup.set(g, []);
        order.push(g);
      }
      byGroup.get(g)!.push(r);
    }
    return order.map((group) => ({ group, reports: byGroup.get(group)! }));
  }, []);
}

function ReportRailItem({
  report,
  active,
}: {
  report: DashboardReport;
  active: boolean;
}) {
  const comingSoon = report.status === "coming-soon";
  return (
    <li>
      <Link href={`/dashboard/${report.id}`}>
        <div
          className={cn(
            "flex items-center justify-between gap-2 px-3 py-2 rounded cursor-pointer text-sm",
            active
              ? "bg-[#0078D4] text-white"
              : "hover:bg-gray-100 text-gray-700",
            comingSoon && !active && "text-gray-400",
          )}
          title={report.description}
        >
          <span className="truncate">{report.title}</span>
          {comingSoon && (
            <span
              className={cn(
                "text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded",
                active ? "bg-white/20 text-white" : "bg-gray-200 text-gray-500",
              )}
            >
              Soon
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

function ComingSoon({ report }: { report: DashboardReport }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-24">
      <span className="material-icons text-4xl text-gray-300 mb-2">
        insights
      </span>
      <h2 className="text-lg font-semibold text-gray-700">{report.title}</h2>
      {report.description && (
        <p className="text-sm text-gray-500 mt-1">{report.description}</p>
      )}
      <p className="text-sm text-gray-400 mt-3">Coming soon</p>
    </div>
  );
}

export default function DashboardPage() {
  const [, params] = useRoute("/dashboard/:reportId");
  const grouped = useGroupedReports();

  const selected =
    getReport(params?.reportId) ?? firstReadyReport() ?? REPORTS[0];

  const ReportComponent = selected?.component;

  return (
    <PageWrapper padding="none">
      <div className="flex h-full">
        {/* LEFT rail — report list (Power BI "Pages") */}
        <aside className="w-60 flex-shrink-0 border-r bg-white overflow-y-auto">
          <div className="px-3 py-3 border-b">
            <h1 className="text-base font-semibold text-gray-800">Dashboard</h1>
            <p className="text-xs text-gray-500">Reports</p>
          </div>
          <nav className="p-2 space-y-3">
            {grouped.map(({ group, reports }) => (
              <div key={group}>
                <div className="text-[11px] uppercase font-semibold text-gray-400 px-2 mb-1">
                  {group}
                </div>
                <ul className="space-y-0.5">
                  {reports.map((r) => (
                    <ReportRailItem
                      key={r.id}
                      report={r}
                      active={selected?.id === r.id}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* RIGHT canvas — selected report */}
        <section className="flex-1 overflow-y-auto bg-[#F5F5F5] p-4">
          {!selected ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              No reports available.
            </div>
          ) : selected.status === "coming-soon" || !ReportComponent ? (
            <ComingSoon report={selected} />
          ) : (
            <Suspense
              fallback={
                <div className="space-y-4">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-64 w-full" />
                </div>
              }
            >
              <ReportComponent />
            </Suspense>
          )}
        </section>
      </div>
    </PageWrapper>
  );
}
